import { useAuthData } from '@anytag/auth';
import { useMatchPaths } from '@anytag/shared-hooks';
import { useTiktokBusinessAccountInfoQuery } from '@anytag/graphql';
import { LinkRouterProps } from '@anytag/router';
import { cn } from '@anytag/shared-utils';

const STEP_ROUTES = [
  '/tiktok-onboarding',
  '/tiktok-onboarding/password-enter',
  '/tiktok-onboarding/select-plan',
  '/tiktok-onboarding/ad-account',
  '/tiktok-onboarding/success'
] as const satisfies LinkRouterProps['to'][];

export type StepType = {
  isActive: boolean;
  isCompleted: boolean;
  isDefault: boolean;
  href: (typeof STEP_ROUTES)[number];
  isDisabled: boolean;
};
export const useTiktokSignupSteps = () => {
  const { auth } = useAuthData();
  const { matchPaths, matchRoute } = useMatchPaths();
  const { data } = useTiktokBusinessAccountInfoQuery({ skip: !auth.userId });

  const isStepInitial = !!matchRoute({ to: '/tiktok-onboarding' });
  const isStepPassword = matchPaths([
    '/tiktok-onboarding/password-enter',
    '/tiktok-onboarding/password-register/$token'
  ]);
  const isStepSuccess = !!matchRoute({ to: '/tiktok-onboarding/success' });
  const isStepOne = isStepInitial || isStepPassword || isStepSuccess;
  const isStepTwo = !!matchRoute({ to: '/tiktok-onboarding/select-plan' });
  const isStepThree = !!matchRoute({ to: '/tiktok-onboarding/ad-account' });

  return {
    stepOne: {
      isActive: isStepOne,
      isCompleted: !!auth.userId,
      isDefault: false,
      href: '/tiktok-onboarding',
      isDisabled: false
    },
    stepTwo: {
      isActive: isStepTwo,
      isCompleted: !!auth.subscriptionPlan,
      isDefault: !auth.subscriptionPlan,
      href: '/tiktok-onboarding/select-plan',
      isDisabled: !auth.userId && !isStepTwo
    },
    stepThree: {
      isActive: isStepThree,
      isCompleted: !!data?.tiktokBusinessAccountInfo?.connected,
      isDefault: !data?.tiktokBusinessAccountInfo?.connected,
      href: '/tiktok-onboarding/ad-account',
      isDisabled: !auth.subscriptionPlan && !isStepThree
    }
  } satisfies Record<string, StepType>;
};

export const getStepStyles = (step: StepType) =>
  step.isActive
    ? cn('border-base-black-90 border-9')
    : step.isDefault
      ? cn(
          'after:absolute after:top-2.25 after:left-2.25 after:h-1 after:w-1 after:rounded-full after:bg-black-60 after:content-[""]'
        )
      : '';
